import React from "react";

const formatTime = (secs) => {
  const s = Math.max(0, Number(secs) || 0);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m > 0) return `${m}m ${rem}s`;
  return `${rem}s`;
};

const ChallengeLeaderboardModal = ({
  isOpen,
  onClose,
  challenge,
  user,
  liveAvatars = {},
  onAcceptChallenge,
}) => {
  if (!isOpen || !challenge) return null;

  const leaderboard = challenge.leaderboard || [];
  const topThree = leaderboard.slice(0, 3);
  const first = topThree[0] || null;
  const second = topThree[1] || null;
  const third = topThree[2] || null;

  const currentUserId = String(user?._id || user?.id || "");
  const userAttempt = leaderboard.find((p) => String(p.userId) === currentUserId);
  const hasAttempted = Boolean(challenge.hasUserAttempted || userAttempt);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-violet-900 via-indigo-900 to-purple-950 text-white flex items-center justify-between shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wide uppercase ${
                  challenge.status === "concluded"
                    ? "bg-emerald-400 text-emerald-950"
                    : challenge.status === "cancelled"
                    ? "bg-rose-400 text-rose-950"
                    : "bg-amber-400 text-amber-950"
                }`}
              >
                {challenge.status === "concluded"
                  ? "Concluded Challenge"
                  : challenge.status === "cancelled"
                  ? "Cancelled Challenge"
                  : "Challenge Standings"}
              </span>
              <span className="text-xs text-indigo-200 font-semibold">
                {challenge.participantsCount || leaderboard.length} participant
                {(challenge.participantsCount || leaderboard.length) !== 1 ? "s" : ""}
              </span>
            </div>
            <h2 className="text-lg md:text-xl font-black tracking-tight">{challenge.title}</h2>
            <p className="text-xs text-indigo-300 font-medium">Module: {challenge.reviewerTitle}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center shrink-0"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-sm"></i>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-6">
          {/* Olympic Podium (Top 3) */}
          {leaderboard.length > 0 ? (
            <div className="bg-gradient-to-b from-indigo-50/70 to-purple-50/40 rounded-2xl p-5 border border-indigo-100/60">
              <div className="flex items-end justify-center gap-2 sm:gap-4 pt-4 pb-2">
                {/* 2nd Place (Silver) */}
                <div className="flex flex-col items-center flex-1 max-w-[130px]">
                  {second ? (
                    <>
                      <div className="relative mb-2">
                        <img
                          src={
                            liveAvatars[String(second.userId)] ||
                            second.avatar ||
                            `https://api.dicebear.com/9.x/adventurer/svg?seed=${second.firstName}&backgroundColor=b6e3f4`
                          }
                          alt={second.firstName}
                          className="w-12 h-12 rounded-full object-cover border-2 border-slate-300 shadow-md"
                        />
                        <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-300 text-slate-800 text-[11px] font-black flex items-center justify-center shadow-xs">
                          2
                        </span>
                      </div>
                      <p className="text-xs font-bold text-gray-800 truncate w-full text-center">
                        {second.firstName} {second.lastName?.[0] || ""}
                      </p>
                      <span className="text-[11px] font-extrabold text-slate-600 font-mono">
                        {second.totalPoints} pts
                      </span>
                      <div className="w-full h-16 bg-gradient-to-t from-slate-200 to-slate-100 rounded-t-xl mt-2 flex items-center justify-center font-black text-slate-400 text-xl border-t border-slate-300">
                        🥈
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-16 rounded-t-xl bg-gray-100/60 border-t border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">
                      Empty
                    </div>
                  )}
                </div>

                {/* 1st Place (Gold - Tallest) */}
                <div className="flex flex-col items-center flex-1 max-w-[140px] -mt-4">
                  {first ? (
                    <>
                      <div className="relative mb-2">
                        <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-xl filter drop-shadow">
                          👑
                        </span>
                        <img
                          src={
                            liveAvatars[String(first.userId)] ||
                            first.avatar ||
                            `https://api.dicebear.com/9.x/adventurer/svg?seed=${first.firstName}&backgroundColor=b6e3f4`
                          }
                          alt={first.firstName}
                          className="w-14 h-14 rounded-full object-cover border-3 border-amber-400 shadow-lg ring-4 ring-amber-100"
                        />
                        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-400 text-amber-950 text-xs font-black flex items-center justify-center shadow-sm">
                          1
                        </span>
                      </div>
                      <p className="text-xs font-extrabold text-gray-900 truncate w-full text-center">
                        {first.firstName} {first.lastName?.[0] || ""}
                      </p>
                      <span className="text-xs font-black text-amber-600 font-mono">
                        {first.totalPoints} pts
                      </span>
                      <div className="w-full h-24 bg-gradient-to-t from-amber-200 to-amber-100 rounded-t-xl mt-2 flex items-center justify-center font-black text-amber-600 text-2xl border-t-2 border-amber-300 shadow-xs">
                        🥇
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-24 rounded-t-xl bg-gray-100/60 border-t border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">
                      No attempts
                    </div>
                  )}
                </div>

                {/* 3rd Place (Bronze) */}
                <div className="flex flex-col items-center flex-1 max-w-[130px]">
                  {third ? (
                    <>
                      <div className="relative mb-2">
                        <img
                          src={
                            liveAvatars[String(third.userId)] ||
                            third.avatar ||
                            `https://api.dicebear.com/9.x/adventurer/svg?seed=${third.firstName}&backgroundColor=b6e3f4`
                          }
                          alt={third.firstName}
                          className="w-12 h-12 rounded-full object-cover border-2 border-amber-700 shadow-md"
                        />
                        <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-700 text-white text-[11px] font-black flex items-center justify-center shadow-xs">
                          3
                        </span>
                      </div>
                      <p className="text-xs font-bold text-gray-800 truncate w-full text-center">
                        {third.firstName} {third.lastName?.[0] || ""}
                      </p>
                      <span className="text-[11px] font-extrabold text-amber-800 font-mono">
                        {third.totalPoints} pts
                      </span>
                      <div className="w-full h-12 bg-gradient-to-t from-amber-100 to-orange-50 rounded-t-xl mt-2 flex items-center justify-center font-black text-amber-800 text-lg border-t border-amber-300">
                        🥉
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-12 rounded-t-xl bg-gray-100/60 border-t border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">
                      Empty
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              <span className="text-4xl block mb-2">⚡</span>
              <h3 className="font-bold text-gray-800 text-sm">No Scores Yet</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                Be the first to jump in and claim #1 on the leaderboard!
              </p>
            </div>
          )}

          {/* Detailed Leaderboard Table */}
          {leaderboard.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                Full Standings ({leaderboard.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 text-gray-400 uppercase font-semibold">
                      <th className="py-2.5 pl-2 text-center w-8">#</th>
                      <th className="py-2.5 px-3">Member</th>
                      <th className="py-2.5 px-3 text-center">Score</th>
                      <th className="py-2.5 px-3 text-center">Time</th>
                      <th className="py-2.5 px-3 text-center">Speed Bonus</th>
                      <th className="py-2.5 pr-2 text-right">Total Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 font-medium">
                    {leaderboard.map((item) => {
                      const isMe = String(item.userId) === currentUserId;
                      return (
                        <tr
                          key={item.attemptId || item.userId}
                          className={`transition ${isMe ? "bg-indigo-50/70 font-bold" : "hover:bg-gray-50"}`}
                        >
                          <td className="py-3 pl-2 text-center font-bold text-gray-500">
                            {item.rank === 1 ? "🥇" : item.rank === 2 ? "🥈" : item.rank === 3 ? "🥉" : `#${item.rank}`}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2.5">
                              <img
                                src={
                                  liveAvatars[String(item.userId)] ||
                                  item.avatar ||
                                  `https://api.dicebear.com/9.x/adventurer/svg?seed=${item.firstName}&backgroundColor=b6e3f4`
                                }
                                alt={item.firstName}
                                className="w-7 h-7 rounded-full object-cover border border-gray-200"
                              />
                              <span className="truncate max-w-[140px] text-gray-800">
                                {item.firstName} {item.lastName || ""} {isMe && "(You)"}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center text-gray-700">
                            {item.score}/{item.totalQuestions} ({item.percentage}%)
                          </td>
                          <td className="py-3 px-3 text-center text-gray-500 font-mono">
                            {formatTime(item.timeSpentSeconds)}
                          </td>
                          <td className="py-3 px-3 text-center text-indigo-600 font-mono font-semibold">
                            +{item.speedBonus || 0} pts
                          </td>
                          <td className="py-3 pr-2 text-right font-bold text-indigo-900 font-mono text-sm">
                            {item.totalPoints} pts
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
          <div>
            {userAttempt ? (
              <span className="text-xs text-indigo-900 font-bold flex items-center gap-1.5">
                <i className="fa-solid fa-medal text-amber-500"></i>
                <span>Your Standing: Rank #{userAttempt.rank} with {userAttempt.totalPoints} pts</span>
              </span>
            ) : challenge.status !== "active" ? (
              <span className="text-xs text-gray-500">This challenge has ended.</span>
            ) : (
              <span className="text-xs text-gray-500">You haven't attempted this challenge yet.</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!hasAttempted && challenge.status === "active" && onAcceptChallenge && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onAcceptChallenge();
                }}
                className="px-4 py-2 rounded-xl text-xs font-black bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-amber-950 shadow-sm transition active:scale-95"
              >
                ⚡ Accept Challenge
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeLeaderboardModal;
